<template>
  <div
    ref="scrollContainer"
    class="relative min-h-screen pb-8 pt-12 lg:py-24"
    :class="images && images.length <= 3 ? 'h-[250vh]' : 'h-[300vh]'">
    <Container
      v-if="title || copy"
      class="relative z-10 flex flex-col justify-center text-white">
      <h2
        v-if="title"
        class="text-4xl leading-tighter lg:w-3/4 lg:text-6xl">
        {{ title }}
      </h2>
      <div
        v-if="copy"
        class="mt-8 text-xl lg:mt-10 lg:w-3/4 lg:text-2xl"
        v-html="copy" />
    </Container>
    <div class="sticky top-20 z-10 overflow-hidden md:top-[15vh]">
      <Container class="relative flex flex-col justify-center text-white">
        <div
          ref="imgContainer"
          class="mt-16 flex min-h-0 max-w-[100vw] flex-1 flex-shrink space-x-8 lg:mt-20 lg:w-[36vw]"
          :class="{ 'pl-[20vw] lg:pl-[25vw]': youtubePadding }"
          :style="{
            opacity: opacityCards
          }">
          <div
            v-for="(img, i) in images"
            :key="i"
            class="w-full flex-shrink-0 lg:w-[36vw]">
            <!-- Wrapper for @tailwindcss/aspect-ratio padding trick -->
            <div class="aspect-h-1 aspect-w-1 md:aspect-h-15 md:aspect-w-22">
              <slot
                name="slide"
                :item="img"
                :index="i">
                <NuxtImg
                  :src="img.src"
                  :alt="img.alt || ''"
                  class="h-full w-full rounded-lg object-cover lg:w-[36vw]"
                  sizes="xs:87vw sm:87vw md:87vw lg:36vw xl:36vw xxl:36vw"
                  loading="lazy" />
              </slot>
            </div>
          </div>
        </div>
        <div
          v-if="outfit7neo"
          class="mx-auto mt-8 flex flex-wrap items-center justify-center gap-4 text-sm md:text-base lg:mt-20"
          :style="{ opacity: opacityO7N }">
          <span class="w-full text-center text-sm text-white md:w-auto md:text-left lg:text-base">Published by:</span>
          <SvgOutfit7neoLogo class="h-8 lg:h-9" />
        </div>
      </Container>
    </div>
    <div class="absolute top-0 h-full w-full">
      <div class="sticky top-0 h-screen overflow-hidden">
        <div class="absolute -left-36 -top-36 z-0">
          <div
            class="h-[100vw] w-[100vw] rounded-full blur-3xl duration-150 md:h-[50vw] md:w-[50vw] md:blur-2xl"
            :class="[gradientTop && !gradientTop.startsWith('#') ? `bg-${gradientTop}` : '']"
            :style="{
              transform: 'scale3d(1, 1, 1)',
              opacity: opacity,
              backgroundColor: gradientTop && gradientTop.startsWith('#') ? gradientTop : undefined
            }" />
        </div>
        <div
          class="absolute left-0 right-0 z-0"
          :style="{
            transform: `translate3d(0, ${position}%, 0)`
          }">
          <div
            class="h-[200vw] w-[200vw] rounded-full blur-3xl duration-150 md:blur-2xl lg:h-[100vw] lg:w-[100vw]"
            :class="[gradientBottom && !gradientBottom.startsWith('#') ? `bg-${gradientBottom}` : '']"
            :style="{
              transform: `scale3d(${scale}, ${scale}, 1)`,
              backgroundColor: gradientBottom && gradientBottom.startsWith('#') ? gradientBottom : undefined
            }" />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { onMounted, ref } from 'vue'

  import { scroll } from 'motion'

  import { useScreenSize } from '../../composables/use-screen-size'

  withDefaults(defineProps<GradientSliderProps>(), {
    title: '',
    copy: '',
    outfit7neo: false,
    gradientTop: '',
    gradientBottom: '',
    youtubePadding: false
  })

  const { isDesktop } = useScreenSize()

  type GradientSliderProps = {
    title?: string
    copy?: string
    images: {
      src: string
      alt?: string
    }[]
    outfit7neo?: boolean
    gradientTop?: string
    gradientBottom?: string
    youtubePadding?: boolean
  }
  const scrollContainer = ref<HTMLElement | null>(null)
  const imgContainer = ref<HTMLElement | null>(null)
  const position = ref(90)
  const scale = ref(1)
  const opacity = ref(1)
  const opacityO7N = ref(0)
  const opacityCards = ref(0)

  const maxXOffset = computed(() => {
    if (!imgContainer.value) {
      return 0
    }
    return isDesktop.value
      ? imgContainer.value.scrollWidth
      : imgContainer.value.scrollWidth -
          imgContainer.value.parentElement.offsetWidth +
          imgContainer.value.offsetLeft * 2
  })

  const animate = (progress: number) => {
    if (typeof window === 'undefined') {
      return
    }
    if (!scrollContainer.value || !imgContainer.value) {
      return
    }

    const xOffset = -(isDesktop.value
      ? progress * maxXOffset.value - imgContainer.value.parentElement.offsetWidth
      : (progress - 0.4) * 1.667 * maxXOffset.value)

    opacity.value = Math.max(0, Math.min(1, 1 - (progress - 0.3) * 4))
    opacityO7N.value = Math.max(Math.min(1, (progress - 0.45) * 7), 0)
    opacityCards.value = Math.max(0, Math.min(1, (progress - 0.25) * 7))
    position.value = (1 - progress / 2) * 100
    scale.value = 1 + (progress - 0.4) * 2

    imgContainer.value.style.transform = `translate3d(${xOffset}px, 0, 0)`
  }

  // center a slide in view by index
  function centerSlide(index: number) {
    if (typeof window === 'undefined') {
      return
    }
    if (!scrollContainer.value || !imgContainer.value) {
      return
    }

    const parent = imgContainer.value.parentElement as HTMLElement
    const children = Array.from(imgContainer.value.children) as HTMLElement[]
    const element = children[index]
    if (!element) {
      return
    }

    // Desired horizontal amount so that the slide center aligns to viewport center
    const targetXOffset = element.offsetLeft + element.offsetWidth / 2

    // Convert desired horizontal amount to target progress depending on layout formula
    const targetProgress = Math.max(
      0,
      Math.min(
        1,
        isDesktop.value
          ? (targetXOffset + parent.offsetWidth / 2) / maxXOffset.value
          : (targetXOffset - parent.offsetWidth / 2) / (1.667 * maxXOffset.value) + 0.4
      )
    )

    // Translate progress to page scrollY: offsets match motion.scroll(['start end','end end'])
    const rect = scrollContainer.value.getBoundingClientRect()
    const sectionStart = rect.top + window.scrollY - window.innerHeight // when top hits bottom of viewport
    const sectionEnd = sectionStart + scrollContainer.value.offsetHeight // when bottom hits bottom

    const targetY = sectionStart + targetProgress * (sectionEnd - sectionStart)

    window.scrollTo({ top: targetY, behavior: 'smooth' })
  }

  onMounted(() => {
    scroll(
      ({ y }) => {
        animate(y.progress)
      },
      {
        target: scrollContainer.value!,
        offset: ['start end', 'end end']
      }
    )
  })

  defineExpose({ centerSlide })
</script>
